import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useLanguage } from '../context/LanguageContext';
import { useAuth } from '../context/AuthContext';
import Layout from '../components/Layout';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '../components/ui/dialog';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '../components/ui/alert-dialog';
import {
  Plus,
  MagnifyingGlass,
  Export,
  FileCsv,
  FileXls,
  FilePdf,
  CheckCircle,
  CaretDown,
  CaretRight,
  CurrencyEur,
  Trash,
  ClockCounterClockwise,
} from '@phosphor-icons/react';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '../components/ui/dropdown-menu';
import { toast } from 'sonner';
import * as XLSX from 'xlsx';
import { saveAs } from 'file-saver';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import DatePickerDMY from '../components/DatePickerDMY';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

const formatDateDMY = (d) => {
  if (!d) return '-';
  const p = d.split('-');
  return p.length === 3 ? `${p[2]}/${p[1]}/${p[0]}` : d;
};

export default function Payments() {
  const { t, language } = useLanguage();
  const { isAdmin } = useAuth();
  const [buildings, setBuildings] = useState([]);
  const [expandedBuildings, setExpandedBuildings] = useState({});
  const [buildingApartments, setBuildingApartments] = useState({});
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [payDialogOpen, setPayDialogOpen] = useState(false);
  const [payingApt, setPayingApt] = useState(null);
  const [payingBuilding, setPayingBuilding] = useState(null);
  const [payForm, setPayForm] = useState({ amount: '', date: '', notes: '' });
  const [historyDialogOpen, setHistoryDialogOpen] = useState(false);
  const [historyApt, setHistoryApt] = useState(null);
  const [historyPayments, setHistoryPayments] = useState([]);
  const [deletePaymentDialogOpen, setDeletePaymentDialogOpen] = useState(false);
  const [deletingPayment, setDeletingPayment] = useState(null);

  useEffect(() => { fetchBuildings(); }, []);

  const fetchBuildings = async () => {
    try {
      const res = await axios.get(`${API}/buildings`, { withCredentials: true });
      setBuildings(res.data);
    } catch (error) {
      toast.error(t('errorOccurred'));
    } finally {
      setLoading(false);
    }
  };

  const fetchApartments = async (buildingId) => {
    try {
      const res = await axios.get(`${API}/buildings/${buildingId}/apartments`, { withCredentials: true });
      setBuildingApartments(prev => ({ ...prev, [buildingId]: res.data }));
    } catch (error) {
      toast.error(t('errorOccurred'));
    }
  };

  const toggleBuilding = (buildingId) => {
    const isExpanded = expandedBuildings[buildingId];
    setExpandedBuildings(prev => ({ ...prev, [buildingId]: !isExpanded }));
    if (!isExpanded && !buildingApartments[buildingId]) {
      fetchApartments(buildingId);
    }
  };

  const openPayDialog = (apt, building) => {
    setPayingApt(apt);
    setPayingBuilding(building);
    const totalDue = (apt.monthly_fee || 0) + (apt.reserve_fund || 0);
    setPayForm({ amount: totalDue.toFixed(2), date: new Date().toISOString().split('T')[0], notes: '' });
    setPayDialogOpen(true);
  };

  const openPaymentHistory = async (apt) => {
    setHistoryApt(apt);
    try {
      const res = await axios.get(`${API}/apartments/${apt.apartment_id}/payments`, { withCredentials: true });
      setHistoryPayments(res.data);
      setHistoryDialogOpen(true);
    } catch (error) {
      toast.error(t('errorOccurred'));
    }
  };

  const handleDeletePayment = async () => {
    if (!deletingPayment) return;
    try {
      await axios.delete(`${API}/apartment-payments/${deletingPayment.payment_id}`, { withCredentials: true });
      toast.success(t('deletedSuccess'));
      setDeletePaymentDialogOpen(false);
      setDeletingPayment(null);
      // Refresh history
      if (historyApt) {
        const res = await axios.get(`${API}/apartments/${historyApt.apartment_id}/payments`, { withCredentials: true });
        setHistoryPayments(res.data);
      }
      // Refresh building apartments
      for (const bid of Object.keys(expandedBuildings)) {
        if (expandedBuildings[bid]) fetchApartments(bid);
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || t('errorOccurred'));
    }
  };

  const handlePay = async (e) => {
    e.preventDefault();
    try {
      await axios.post(`${API}/apartments/${payingApt.apartment_id}/payments`, {
        amount: parseFloat(payForm.amount),
        date: payForm.date,
        notes: payForm.notes,
      }, { withCredentials: true });
      toast.success(t('savedSuccess'));
      setPayDialogOpen(false);
      // Refresh apartments for this building
      if (payingBuilding) fetchApartments(payingBuilding.building_id);
    } catch (error) {
      toast.error(error.response?.data?.detail || t('errorOccurred'));
    }
  };

  const getStatusBadge = (apt) => {
    const totalDue = (apt.monthly_fee || 0) + (apt.reserve_fund || 0);
    const paid = apt.total_paid || 0;
    const balance = paid - totalDue;
    if (totalDue === 0) return { label: '-', cls: 'bg-zinc-50 text-zinc-400 border-zinc-200' };
    if (balance >= 0) return { label: t('paid'), cls: 'bg-emerald-50 text-emerald-700 border-emerald-300' };
    if (paid > 0) return { label: t('pending'), cls: 'bg-amber-50 text-amber-700 border-amber-300' };
    return { label: t('overdue'), cls: 'bg-red-50 text-red-700 border-red-300' };
  };

  const getRowBg = (apt) => {
    const totalDue = (apt.monthly_fee || 0) + (apt.reserve_fund || 0);
    const paid = apt.total_paid || 0;
    if (totalDue === 0) return '';
    if (paid >= totalDue) return 'bg-emerald-50/30';
    if (paid > 0) return 'bg-amber-50/20';
    return 'bg-red-50/20';
  };

  // Totals across all loaded apartments
  const allApts = Object.values(buildingApartments).flat();
  const totalDueAll = allApts.reduce((s, a) => s + (a.monthly_fee || 0) + (a.reserve_fund || 0), 0);
  const totalPaidAll = allApts.reduce((s, a) => s + (a.total_paid || 0), 0);
  const totalBalanceAll = totalPaidAll - totalDueAll;

  const filteredBuildings = buildings.filter(b =>
    b.name?.toLowerCase().includes(search.toLowerCase()) ||
    b.location?.toLowerCase().includes(search.toLowerCase())
  );

  // Export all loaded data
  const exportToCSV = () => {
    const rows = [];
    filteredBuildings.forEach(b => {
      const apts = buildingApartments[b.building_id] || [];
      apts.forEach(a => {
        const totalDue = (a.monthly_fee || 0) + (a.reserve_fund || 0);
        rows.push({
          [t('buildings')]: b.name,
          [t('apartment')]: a.apartment_number,
          [t('ownerName')]: a.owner_name || '',
          [t('sharePercentage')]: a.share_percentage || 0,
          [t('monthlyFee')]: a.monthly_fee,
          [t('reserveFund')]: a.reserve_fund || 0,
          [t('totalDue')]: totalDue,
          [t('paid')]: a.total_paid || 0,
          [t('lastPaymentDate')]: a.last_payment_date ? formatDateDMY(a.last_payment_date) : '-',
          [t('balance')]: (a.total_paid || 0) - totalDue,
        });
      });
    });
    const ws = XLSX.utils.json_to_sheet(rows);
    const csv = XLSX.utils.sheet_to_csv(ws);
    saveAs(new Blob([csv], { type: 'text/csv;charset=utf-8' }), 'payments.csv');
  };

  const exportToExcel = () => {
    const rows = [];
    filteredBuildings.forEach(b => {
      const apts = buildingApartments[b.building_id] || [];
      apts.forEach(a => {
        const totalDue = (a.monthly_fee || 0) + (a.reserve_fund || 0);
        rows.push([b.name, a.apartment_number, a.owner_name || '', a.share_percentage || 0, a.monthly_fee, a.reserve_fund || 0, totalDue, a.total_paid || 0, a.last_payment_date ? formatDateDMY(a.last_payment_date) : '-', (a.total_paid || 0) - totalDue]);
      });
    });
    const ws = XLSX.utils.aoa_to_sheet([
      ['Building', 'Apartment', 'Owner', 'Share %', 'Monthly €', 'Reserve €', 'Total Due €', 'Paid €', 'Last Payment', 'Balance €'],
      ...rows
    ]);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Payments');
    XLSX.writeFile(wb, 'payments.xlsx');
  };

  const exportToPDF = () => {
    const doc = new jsPDF();
    doc.text(t('payments'), 14, 15);
    const rows = [];
    filteredBuildings.forEach(b => {
      const apts = buildingApartments[b.building_id] || [];
      apts.forEach(a => {
        const totalDue = (a.monthly_fee || 0) + (a.reserve_fund || 0);
        rows.push([b.name, a.apartment_number, a.owner_name || '', `${a.share_percentage || 0}%`, `€${a.monthly_fee}`, `€${a.reserve_fund || 0}`, `€${totalDue}`, `€${a.total_paid || 0}`, a.last_payment_date ? formatDateDMY(a.last_payment_date) : '-', `€${((a.total_paid || 0) - totalDue).toFixed(2)}`]);
      });
    });
    autoTable(doc, {
      startY: 25,
      head: [['Building', 'Apt', 'Owner', 'Share', 'Fee', 'Reserve', 'Due', 'Paid', 'Last Pay', 'Balance']],
      body: rows,
      styles: { fontSize: 7 },
      headStyles: { fillColor: [24, 24, 27] }
    });
    doc.save('payments.pdf');
  };

  if (loading) {
    return <Layout><div className="flex items-center justify-center h-64"><div className="text-zinc-500">{t('loading')}</div></div></Layout>;
  }

  return (
    <Layout>
      <div data-testid="payments-page">
        {/* Summary Cards */}
        {allApts.length > 0 && (
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-6">
            <div className="bg-amber-50 border border-amber-200 p-4">
              <p className="text-amber-700 text-sm">{t('totalDue')}</p>
              <p className="text-2xl font-bold text-amber-900 font-mono" data-testid="total-due">&euro;{totalDueAll.toFixed(2)}</p>
            </div>
            <div className="bg-emerald-50 border border-emerald-200 p-4">
              <p className="text-emerald-700 text-sm">{t('paid')}</p>
              <p className="text-2xl font-bold text-emerald-900 font-mono" data-testid="total-paid">&euro;{totalPaidAll.toFixed(2)}</p>
            </div>
            <div className={`border p-4 ${totalBalanceAll >= 0 ? 'bg-emerald-50 border-emerald-200' : 'bg-red-50 border-red-200'}`}>
              <p className={`text-sm ${totalBalanceAll >= 0 ? 'text-emerald-700' : 'text-red-700'}`}>{t('balance')}</p>
              <p className={`text-2xl font-bold font-mono ${totalBalanceAll >= 0 ? 'text-emerald-900' : 'text-red-900'}`} data-testid="total-balance">&euro;{totalBalanceAll.toFixed(2)}</p>
            </div>
          </div>
        )}

        {/* Header */}
        <div className="flex flex-col sm:flex-row gap-4 mb-6">
          <div className="relative flex-1">
            <MagnifyingGlass size={20} className="absolute left-3 top-1/2 -translate-y-1/2 text-zinc-400" />
            <Input
              type="text"
              placeholder={t('search') + '...'}
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="pl-10 border-zinc-200 rounded-none"
              data-testid="search-input"
            />
          </div>
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="outline" className="border-zinc-200 rounded-none" data-testid="export-btn">
                <Export size={18} className="mr-2" />{t('export')}
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              <DropdownMenuItem onClick={exportToCSV}><FileCsv size={18} className="mr-2" />CSV</DropdownMenuItem>
              <DropdownMenuItem onClick={exportToExcel}><FileXls size={18} className="mr-2" />Excel</DropdownMenuItem>
              <DropdownMenuItem onClick={exportToPDF}><FilePdf size={18} className="mr-2" />PDF</DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>

        {/* Buildings List */}
        <div className="space-y-3">
          {filteredBuildings.length === 0 ? (
            <div className="bg-white border border-zinc-200 p-16 text-center text-zinc-500">{t('noBuildings')}</div>
          ) : (
            filteredBuildings.map((building) => {
              const isExpanded = expandedBuildings[building.building_id];
              const apts = buildingApartments[building.building_id] || [];
              const bTotalDue = apts.reduce((s, a) => s + (a.monthly_fee || 0) + (a.reserve_fund || 0), 0);
              const bTotalPaid = apts.reduce((s, a) => s + (a.total_paid || 0), 0);

              return (
                <div key={building.building_id} className="bg-white border border-zinc-200" data-testid={`building-payment-${building.building_id}`}>
                  {/* Building Header Row */}
                  <button
                    onClick={() => toggleBuilding(building.building_id)}
                    className="w-full flex items-center gap-4 px-5 py-4 hover:bg-zinc-50 transition-colors text-left"
                    data-testid={`toggle-building-${building.building_id}`}
                  >
                    <div className="flex-shrink-0 text-zinc-400">
                      {isExpanded ? <CaretDown size={20} weight="bold" /> : <CaretRight size={20} weight="bold" />}
                    </div>
                    <div className="flex-1 min-w-0">
                      <h3 className="font-semibold" style={{ fontFamily: 'var(--font-heading)' }}>{building.name}</h3>
                      <p className="text-xs text-zinc-400">{building.location}</p>
                    </div>
                    {isExpanded && apts.length > 0 && (
                      <div className="hidden sm:flex gap-6 text-right">
                        <div>
                          <p className="text-xs text-zinc-400">{t('totalDue')}</p>
                          <p className="font-mono font-semibold text-sm">&euro;{bTotalDue.toFixed(2)}</p>
                        </div>
                        <div>
                          <p className="text-xs text-zinc-400">{t('paid')}</p>
                          <p className="font-mono font-semibold text-sm text-emerald-700">&euro;{bTotalPaid.toFixed(2)}</p>
                        </div>
                        <div>
                          <p className="text-xs text-zinc-400">{t('balance')}</p>
                          <p className={`font-mono font-semibold text-sm ${bTotalPaid - bTotalDue >= 0 ? 'text-emerald-700' : 'text-red-700'}`}>
                            &euro;{(bTotalPaid - bTotalDue).toFixed(2)}
                          </p>
                        </div>
                      </div>
                    )}
                    <span className="text-xs font-mono text-zinc-400">{apts.length > 0 ? `${apts.length} ${t('apartments')}` : ''}</span>
                  </button>

                  {/* Expanded Apartments Table */}
                  {isExpanded && (
                    <div className="border-t border-zinc-200">
                      {!buildingApartments[building.building_id] ? (
                        <div className="p-6 text-center text-zinc-400">{t('loading')}</div>
                      ) : apts.length === 0 ? (
                        <div className="p-8 text-center text-zinc-400">{t('noApartments')}</div>
                      ) : (
                        <div className="overflow-x-auto">
                          <table className="excel-table" data-testid={`apartments-payment-table-${building.building_id}`}>
                            <thead>
                              <tr className="bg-zinc-50">
                                <th className="text-xs">{t('apartment')}</th>
                                <th className="text-xs">{t('ownerName')}</th>
                                <th className="text-xs">{t('sharePercentage')}</th>
                                <th className="text-xs">{t('monthlyFee')}</th>
                                <th className="text-xs">{t('reserveFund')}</th>
                                <th className="text-xs">{t('totalDue')}</th>
                                <th className="text-xs">{t('paid')}</th>
                                <th className="text-xs">{t('lastPaymentDate')}</th>
                                <th className="text-xs">{t('balance')}</th>
                                <th className="text-xs">{t('status')}</th>
                                <th className="text-xs w-32">{t('edit')}</th>
                              </tr>
                            </thead>
                            <tbody>
                              {apts.map((apt) => {
                                const totalDue = (apt.monthly_fee || 0) + (apt.reserve_fund || 0);
                                const paid = apt.total_paid || 0;
                                const balance = paid - totalDue;
                                const status = getStatusBadge(apt);

                                return (
                                  <tr key={apt.apartment_id} className={getRowBg(apt)} data-testid={`apt-payment-row-${apt.apartment_id}`}>
                                    <td className="font-semibold">{apt.apartment_number}</td>
                                    <td className="text-sm text-zinc-600">{apt.owner_name || <span className="text-zinc-300">—</span>}</td>
                                    <td className="font-mono text-sm">{(apt.share_percentage || 0).toFixed(2)}%</td>
                                    <td className="font-mono text-sm">&euro;{(apt.monthly_fee || 0).toFixed(2)}</td>
                                    <td className="font-mono text-sm">&euro;{(apt.reserve_fund || 0).toFixed(2)}</td>
                                    <td className="font-mono font-semibold">&euro;{totalDue.toFixed(2)}</td>
                                    <td className="font-mono text-emerald-700">&euro;{paid.toFixed(2)}</td>
                                    <td className="text-sm text-zinc-600" data-testid={`last-payment-date-${apt.apartment_id}`}>
                                      {apt.last_payment_date ? formatDateDMY(apt.last_payment_date) : <span className="text-zinc-400">—</span>}
                                    </td>
                                    <td className={`font-mono font-semibold ${balance >= 0 ? 'text-emerald-700' : 'text-red-700'}`}>
                                      &euro;{balance.toFixed(2)}
                                    </td>
                                    <td>
                                      <span className={`px-2 py-0.5 text-xs font-mono uppercase border ${status.cls}`}>
                                        {status.label}
                                      </span>
                                    </td>
                                    <td>
                                      <div className="flex gap-1">
                                        {balance < 0 && (
                                          <Button
                                            size="sm"
                                            onClick={() => openPayDialog(apt, building)}
                                            className="bg-emerald-600 hover:bg-emerald-700 rounded-none text-xs"
                                            data-testid={`pay-btn-${apt.apartment_id}`}
                                          >
                                            <CurrencyEur size={14} className="mr-1" />
                                            {t('pay')}
                                          </Button>
                                        )}
                                        {apt.payment_count > 0 && (
                                          <button
                                            onClick={() => openPaymentHistory(apt)}
                                            className="p-1.5 hover:bg-zinc-100 text-zinc-500 transition-colors"
                                            title={t('paymentHistory')}
                                            data-testid={`history-btn-${apt.apartment_id}`}
                                          >
                                            <ClockCounterClockwise size={16} />
                                          </button>
                                        )}
                                      </div>
                                    </td>
                                  </tr>
                                );
                              })}
                            </tbody>
                            <tfoot>
                              <tr className="bg-zinc-50 font-semibold">
                                <td colSpan={3} className="text-right text-sm">{t('total')}</td>
                                <td className="font-mono text-sm">&euro;{apts.reduce((s, a) => s + (a.monthly_fee || 0), 0).toFixed(2)}</td>
                                <td className="font-mono text-sm">&euro;{apts.reduce((s, a) => s + (a.reserve_fund || 0), 0).toFixed(2)}</td>
                                <td className="font-mono">&euro;{bTotalDue.toFixed(2)}</td>
                                <td className="font-mono text-emerald-700">&euro;{bTotalPaid.toFixed(2)}</td>
                                <td></td>
                                <td className={`font-mono ${bTotalPaid - bTotalDue >= 0 ? 'text-emerald-700' : 'text-red-700'}`}>
                                  &euro;{(bTotalPaid - bTotalDue).toFixed(2)}
                                </td>
                                <td colSpan={2}></td>
                              </tr>
                            </tfoot>
                          </table>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              );
            })
          )}
        </div>

        {/* Pay Dialog */}
        <Dialog open={payDialogOpen} onOpenChange={setPayDialogOpen}>
          <DialogContent className="sm:max-w-md">
            <DialogHeader>
              <DialogTitle style={{ fontFamily: 'var(--font-heading)' }}>
                {t('recordPayment')} — {payingApt?.apartment_number} ({payingBuilding?.name})
              </DialogTitle>
            </DialogHeader>
            <form onSubmit={handlePay} className="space-y-4">
              {payingApt && (
                <div className="bg-zinc-50 border border-zinc-200 p-3 space-y-1 text-sm">
                  <div className="flex justify-between">
                    <span className="text-zinc-500">{t('monthlyFee')}</span>
                    <span className="font-mono">&euro;{(payingApt.monthly_fee || 0).toFixed(2)}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-zinc-500">{t('reserveFund')}</span>
                    <span className="font-mono">&euro;{(payingApt.reserve_fund || 0).toFixed(2)}</span>
                  </div>
                  <div className="flex justify-between font-semibold border-t border-zinc-200 pt-1">
                    <span>{t('totalDue')}</span>
                    <span className="font-mono">&euro;{((payingApt.monthly_fee || 0) + (payingApt.reserve_fund || 0)).toFixed(2)}</span>
                  </div>
                </div>
              )}
              <div>
                <Label htmlFor="pay_amount">{t('amount')} (&euro;)</Label>
                <Input
                  id="pay_amount"
                  type="number"
                  step="0.01"
                  min="0"
                  value={payForm.amount}
                  onChange={(e) => setPayForm({ ...payForm, amount: e.target.value })}
                  className="mt-1 rounded-none font-mono"
                  required
                  data-testid="pay-amount-input"
                />
              </div>
              <div>
                <Label htmlFor="pay_date">{t('date')}</Label>
                <DatePickerDMY
                  value={payForm.date}
                  onChange={(v) => setPayForm({ ...payForm, date: v })}
                  className="mt-1 font-mono"
                  data-testid="pay-date-input"
                />
              </div>
              <div>
                <Label htmlFor="pay_notes">{t('notes')}</Label>
                <Textarea
                  id="pay_notes"
                  value={payForm.notes}
                  onChange={(e) => setPayForm({ ...payForm, notes: e.target.value })}
                  className="mt-1 rounded-none"
                  rows={2}
                  data-testid="pay-notes-input"
                />
              </div>
              <DialogFooter>
                <Button type="button" variant="outline" onClick={() => setPayDialogOpen(false)} className="rounded-none">
                  {t('cancel')}
                </Button>
                <Button type="submit" className="bg-emerald-600 hover:bg-emerald-700 rounded-none" data-testid="confirm-pay-btn">
                  <CheckCircle size={18} className="mr-2" />{t('recordPayment')}
                </Button>
              </DialogFooter>
            </form>
          </DialogContent>
        </Dialog>

        {/* Payment History Dialog */}
        <Dialog open={historyDialogOpen} onOpenChange={setHistoryDialogOpen}>
          <DialogContent className="sm:max-w-lg max-h-[80vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle style={{ fontFamily: 'var(--font-heading)' }}>
                {t('paymentHistory')} — {historyApt?.apartment_number}
              </DialogTitle>
            </DialogHeader>
            {historyPayments.length === 0 ? (
              <p className="text-center text-zinc-500 py-6">{t('noPayments')}</p>
            ) : (
              <table className="excel-table" data-testid="payment-history-table">
                <thead>
                  <tr>
                    <th className="text-xs">{t('date')}</th>
                    <th className="text-xs text-right">{t('amount')}</th>
                    <th className="text-xs">{t('notes')}</th>
                    <th className="text-xs w-12"></th>
                  </tr>
                </thead>
                <tbody>
                  {historyPayments.map((p) => (
                    <tr key={p.payment_id} data-testid={`payment-history-row-${p.payment_id}`}>
                      <td className="font-mono text-sm">{formatDateDMY(p.date)}</td>
                      <td className="font-mono text-sm text-right text-emerald-700">&euro;{(p.amount || 0).toFixed(2)}</td>
                      <td className="text-sm text-zinc-500">{p.notes || '—'}</td>
                      <td>
                        {isAdmin && (
                          <button
                            onClick={() => { setDeletingPayment(p); setDeletePaymentDialogOpen(true); }}
                            className="p-1.5 hover:bg-red-50 text-red-600 transition-colors"
                            title={t('delete')}
                            data-testid={`delete-payment-${p.payment_id}`}
                          >
                            <Trash size={16} />
                          </button>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </DialogContent>
        </Dialog>

        {/* Delete Payment Confirmation */}
        <AlertDialog open={deletePaymentDialogOpen} onOpenChange={setDeletePaymentDialogOpen}>
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle>{t('deletePayment')}</AlertDialogTitle>
              <AlertDialogDescription>{t('confirmDelete')}</AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel className="rounded-none">{t('cancel')}</AlertDialogCancel>
              <AlertDialogAction onClick={handleDeletePayment} className="bg-red-600 rounded-none" data-testid="confirm-delete-payment-btn">
                {t('delete')}
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      </div>
    </Layout>
  );
}
